import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import getDataVisualization from '../../services/dataVisualizationService';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ChevronLeft, BarChart2, PieChart as PieChartIcon, Users, FileText, Globe, Activity, Calendar, Loader } from 'lucide-react';

const DataVisualizationPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('participantsPerStudy');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getDataVisualization();
        setData(response);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('An error occurred while fetching data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const COLORS = ['#007764', '#009982', '#00bba0', '#00ddbe', '#00ffdc', '#1affdf'];

  const renderChart = () => {
    if (!data) return null;

    const chartConfig = {
      participantsPerStudy: {
        type: 'bar',
        data: data.studies,
        xKey: 'title',
        bars: [
          { dataKey: 'participant_count', fill: '#007764', name: 'Total Participants' },
          { dataKey: 'active_participant_count', fill: '#00bba0', name: 'Active Participants' }
        ]
      },
      sitesPerStudy: {
        type: 'bar',
        data: data.studies,
        xKey: 'title',
        bars: [{ dataKey: 'site_count', fill: '#007764', name: 'Sites' }]
      },
      studyStatus: {
        type: 'pie',
        data: Object.entries(data.study_status_distribution).map(([name, value]) => ({ name, value }))
      },
      participantsByCountry: {
        type: 'bar',
        data: Object.entries(data.participants_by_country).map(([name, value]) => ({ name, value })),
        xKey: 'name',
        bars: [{ dataKey: 'value', fill: '#007764', name: 'Participants' }]
      },
      byodDistribution: {
        type: 'pie',
        data: Object.entries(data.byod_distribution).map(([name, value]) => ({ name, value }))
      },
      participantActivity: {
        type: 'pie',
        data: Object.entries(data.participant_activity).map(([name, value]) => ({ name, value }))
      },
      surveysPerStudy: {
        type: 'bar',
        data: Object.entries(data.surveys_per_study).map(([name, value]) => ({ name, value })),
        xKey: 'name',
        bars: [{ dataKey: 'value', fill: '#007764', name: 'Surveys' }]
      },
      dcqStatusDistribution: {
        type: 'pie',
        data: Object.entries(data.dcq_status_distribution).map(([name, value]) => ({ name, value }))
      }
    };

    const config = chartConfig[activeChart];

    if (config.type === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={config.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0f2f1" />
            <XAxis dataKey={config.xKey} stroke="#004d40" />
            <YAxis stroke="#004d40" />
            <Tooltip contentStyle={{ backgroundColor: '#e0f2f1', border: 'none', borderRadius: '8px' }} />
            <Legend />
            {config.bars.map((bar, index) => (
              <Bar key={index} dataKey={bar.dataKey} fill={bar.fill} name={bar.name} radius={[4, 4, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    } else if (config.type === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={config.data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={150}
              fill="#007764"
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {config.data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: '#e0f2f1', border: 'none', borderRadius: '8px' }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
  };

  const TabButton = ({ name, icon: Icon, label }) => (
    <button
      onClick={() => setActiveChart(name)}
      className={`flex items-center px-4 py-2 rounded-md transition-all duration-200 ease-in-out ${
        activeChart === name ? 'bg-primary-600 dark:bg-primary-700 text-white shadow-md' : 'bg-white text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
      }`}
    >
      <Icon size={18} className="mr-2" />
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-white border-l-4 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 p-4 rounded-md shadow-md" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 dark:text-primary-400">Data Visualization</h1>
          <Link to="/" className="text-primary-600 dark:text-primary-400 hover:text-primary-400 transition duration-150 ease-in-out flex items-center">
            <ChevronLeft size={20} className="mr-1" />
            Back to Dashboard
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-wrap gap-2 mb-6">
            <TabButton name="participantsPerStudy" icon={Users} label="Participants per Study" />
            <TabButton name="sitesPerStudy" icon={BarChart2} label="Sites per Study" />
            <TabButton name="studyStatus" icon={PieChartIcon} label="Study Status" />
            <TabButton name="participantsByCountry" icon={Globe} label="Participants by Country" />
            <TabButton name="byodDistribution" icon={Activity} label="BYOD Distribution" />
            <TabButton name="participantActivity" icon={Users} label="Participant Activity" />
            <TabButton name="surveysPerStudy" icon={FileText} label="Surveys per Study" />
            <TabButton name="dcqStatusDistribution" icon={PieChartIcon} label="DCQ Status" />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeChart}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderChart()}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">Total Studies</h3>
            <p className="text-3xl font-bold text-[#00bba0]">{data.total_studies}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">Total Sites</h3>
            <p className="text-3xl font-bold text-[#00bba0]">{data.total_sites}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">Total Participants</h3>
            <p className="text-3xl font-bold text-[#00bba0]">{data.total_participants}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">Avg. Participants per Site</h3>
            <p className="text-3xl font-bold text-[#00bba0]">{data.avg_participants_per_site.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">Active Participants</h3>
            <p className="text-3xl font-bold text-[#00bba0]">{data.participant_activity.active}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">BYOD Participants</h3>
            <p className="text-3xl font-bold text-[#00bba0]">{data.byod_distribution.BYOD}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataVisualizationPage;
